import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TableSortLabel, CircularProgress,
    IconButton, Tooltip, Chip
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { axios_tenant } from "../../../../axios";
import OAPagination from "../../../shared/OAWorkflow/components/OAPagination";
import { formatOADateTime } from "../../../shared/OAWorkflow/utils/dateUtils";
import { BRAND_COLORS } from "../../../shared/OAWorkflow/constants/taskStatuses";
import Button from "../../../shared/OAWorkflow/components/atoms/Button";
import { useDocumentTitle } from "../../../shared/OAWorkflow/utils/useDocumentTitle";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import "./OAWorkflow.css";

const BASE_URL = "/collaboration/workflow";

/**
 * Derives a human-readable file type badge from the file extension.
 */
const getFileTypeBadge = (fileName) => {
    if (!fileName) return { label: "—", color: "#94A3B8" };
    const ext = fileName.split(".").pop().toLowerCase();
    const map = {
        xlsx: { label: "XLSX", color: "#1565C0" },
        xls: { label: "XLS", color: "#1565C0" },
        csv: { label: "CSV", color: "#2E7D32" },
        msg: { label: "MSG", color: "#E65100" },
        pdf: { label: "PDF", color: "#C62828" }
    };
    return map[ext] || { label: ext.toUpperCase(), color: "#546E7A" };
};

/**
 * Returns the status chip config for a batch.
 */
const getStatusChip = (batch) => {
    if (batch.status === "Failed" || batch.importedCount === 0) {
        return { label: "Failed", color: "#C62828", bg: "#FFEBEE" };
    }
    if (batch.status === "Partial" || (batch.rejectedCount > 0 && batch.importedCount > 0)) {
        return { label: "Partial", color: "#E65100", bg: "#FFF3E0" };
    }
    if (batch.status === "Completed") {
        return { label: "Pass", color: "#2E7D32", bg: "#E8F5E9" };
    }
    return { label: batch.status || "—", color: "#546E7A", bg: "#F1F5F9" };
};

/**
 * PGS Validation Results page — shows import batches for Post Grant Support.
 * Route: /collaboration-space/pgs-workflow/validation-results
 * Restricted to Admin + Application Manager.
 */
const PGSValidationResults = () => {
    useDocumentTitle("Validation Results");
    const navigate = useNavigate();
    const outerTheme = useTheme();

    const localTheme = React.useMemo(() => createTheme(outerTheme, {
        typography: { fontFamily: 'Lato, sans-serif' },
    }), [outerTheme]);

    const [batches, setBatches] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);

    // Sorting state
    const [orderBy, setOrderBy] = useState("uploadedAt");
    const [order, setOrder] = useState("desc");

    const fetchBatches = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios_tenant.get(`${BASE_URL}/intake/batches`, {
                params: {
                    page: page + 1,
                    limit: rowsPerPage,
                    sortBy: orderBy,
                    sortOrder: order,
                    workType: "Post Grant Support"
                }
            });
            setBatches(res.data.data || []);
            setTotalRecords(res.data.pagination?.totalRecords || 0);
        } catch (err) {
            console.error("Failed to fetch PGS batches:", err);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, orderBy, order]);

    useEffect(() => {
        fetchBatches();
    }, [fetchBatches]);

    const handleDownloadReport = async (batchId) => {
        try {
            setDownloadingId(batchId);
            const response = await axios_tenant.get(`${BASE_URL}/intake/batches/${batchId}/export`, {
                responseType: 'blob',
                silent: true
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `PGS_Validation_Result_${batchId}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (err) {
            console.error("Failed to download validation report:", err);
            alert("Failed to download validation report.");
        } finally {
            setDownloadingId(null);
        }
    };

    const handleDeleteBatch = async (batchId) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this batch record?");
        if (!confirmDelete) return;

        try {
            await axios_tenant.delete(`${BASE_URL}/intake/batches/${batchId}`, { silent: true });
            fetchBatches();
        } catch (err) {
            console.error("Failed to delete batch:", err);
            alert("Failed to delete the batch.");
        }
    };

    const handleSort = (column) => {
        if (orderBy === column) {
            setOrder(prev => prev === "asc" ? "desc" : "asc");
        } else {
            setOrderBy(column);
            setOrder("asc");
        }
        setPage(0);
    };

    const headCells = [
        { id: "fileName", label: "File Name", sortable: true },
        { id: "fileType", label: "Type", sortable: false, align: "center" },
        { id: "clientName", label: "Client", sortable: true },
        { id: "totalRecords", label: "Total Parsed", sortable: false, align: "center" },
        { id: "importedCount", label: "Created", sortable: false, align: "center" },
        { id: "rejectedCount", label: "Skipped", sortable: false, align: "center" },
        { id: "status", label: "Status", sortable: true, align: "center" },
        { id: "uploadedAt", label: "Date/Time", sortable: true },
        { id: "uploadedBy", label: "Uploaded By", sortable: true },
        { id: "action", label: "Actions", sortable: false, align: "center" },
    ];

    return (
        <ThemeProvider theme={localTheme}>
            <Box className="oa-lato-font" sx={{ p: 4, pb: 10 }} data-testid="pgs-validation-results-page">
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Typography variant="h5" fontWeight={700} sx={{ fontSize: "20px" }}>
                        Validation Results
                    </Typography>
                </Box>

                <Box sx={{ position: "relative", width: "100%" }}>
                    <TableContainer
                        sx={{
                            borderRadius: 2,
                            border: "1px solid #E2E8F0",
                            backgroundColor: "#fff"
                        }}
                    >
                        {loading && (
                            <Box sx={{
                                position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                                backgroundColor: "rgba(255,255,255,0.7)",
                                zIndex: 10, display: "flex", justifyContent: "center", alignItems: "center",
                                minHeight: 150
                            }}>
                                <CircularProgress size={32} sx={{ color: BRAND_COLORS.PRIMARY }} />
                            </Box>
                        )}
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {headCells.map((cell) => (
                                        <TableCell
                                            key={cell.id}
                                            align={cell.align || "left"}
                                            sx={{
                                                fontWeight: 500,
                                                fontSize: "14px",
                                                color: "#64748B",
                                                backgroundColor: "#F8FAFC",
                                                borderBottom: "1px solid #E2E8F0",
                                                whiteSpace: "nowrap"
                                            }}
                                            sortDirection={orderBy === cell.id ? order : false}
                                        >
                                            {cell.sortable ? (
                                                <TableSortLabel
                                                    active={orderBy === cell.id}
                                                    direction={orderBy === cell.id ? order : "asc"}
                                                    onClick={() => handleSort(cell.id)}
                                                    IconComponent={() => (
                                                        <span style={{ fontSize: '14px', marginLeft: '4px' }}>
                                                            {orderBy === cell.id ? (order === 'desc' ? '↓' : '↑') : '↓'}
                                                        </span>
                                                    )}
                                                    sx={{
                                                        '&:hover': { color: 'inherit' },
                                                        '&.Mui-active': { color: 'inherit' },
                                                        '& .MuiTableSortLabel-icon': {
                                                            opacity: orderBy === cell.id ? 1 : 0.3,
                                                            color: '#666'
                                                        }
                                                    }}
                                                >
                                                    {cell.label}
                                                </TableSortLabel>
                                            ) : (
                                                cell.label
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {batches.length === 0 ? (
                                    !loading && (
                                        <TableRow>
                                            <TableCell colSpan={headCells.length} align="center">
                                                <Typography color="text.secondary" sx={{ py: 4 }}>
                                                    No validation results found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )
                                ) : (
                                    batches.map((batch) => {
                                        const fileType = getFileTypeBadge(batch.fileName);
                                        const statusChip = getStatusChip(batch);

                                        return (
                                            <TableRow
                                                key={batch.id}
                                                hover
                                                data-testid={`pgs-batch-row-${batch.id}`}
                                                sx={{ "&:hover": { backgroundColor: "#F8FAFC" } }}
                                            >
                                                <TableCell sx={{ fontSize: "14px", color: "#334155", maxWidth: 260 }}>
                                                    <Typography noWrap sx={{ fontSize: "14px" }} title={batch.fileName}>
                                                        {batch.fileName || "—"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={fileType.label}
                                                        size="small"
                                                        sx={{
                                                            height: 22, fontSize: "10px", fontWeight: 700,
                                                            bgcolor: fileType.color + "18",
                                                            color: fileType.color,
                                                            borderRadius: 1
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontSize: "14px", color: "#334155" }}>
                                                    {batch.clientName
                                                        ? `${batch.clientName} (${batch.tenantCode})`
                                                        : (batch.tenantCode || "—")}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontSize: "14px", color: "#334155" }}>
                                                    {batch.totalRecords ?? 0}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontSize: "14px", fontWeight: 600, color: "#2E7D32" }}>
                                                    {batch.importedCount ?? 0}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontSize: "14px", color: "#94A3B8" }}>
                                                    {batch.rejectedCount ?? 0}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={statusChip.label}
                                                        size="small"
                                                        sx={{
                                                            height: 24, fontSize: "11px", fontWeight: 600,
                                                            bgcolor: statusChip.bg, color: statusChip.color,
                                                            borderRadius: 1
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontSize: "14px", color: "#334155" }}>
                                                    {batch.uploadedAt ? formatOADateTime(batch.uploadedAt) : "—"}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: "14px", color: "#334155" }}>
                                                    {batch.uploadedBy || "—"}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                                                        <Tooltip title="Download Report" arrow>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDownloadReport(batch.id)}
                                                                disabled={downloadingId === batch.id}
                                                                sx={{ color: BRAND_COLORS.PRIMARY }}
                                                            >
                                                                {downloadingId === batch.id ? (
                                                                    <CircularProgress size={20} color="inherit" />
                                                                ) : (
                                                                    <DownloadIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete" arrow>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteBatch(batch.id)}
                                                                sx={{ color: "#64748B" }}
                                                            >
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {batches.length > 0 && (
                        <OAPagination
                            count={totalRecords}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={setPage}
                            onRowsPerPageChange={(val) => { setRowsPerPage(val); setPage(0); }}
                        />
                    )}
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default PGSValidationResults;
